import { ReactElement, useEffect, useState } from 'react';
import { Component as AppleIcon } from 'icons/icon-social--apple.svg';
import { Component as FacebookIcon } from 'icons/icon-social--facebookbtn.svg';
import { Component as GoogleIcon } from 'payment-img/google-pay/google-pay-icon.svg';
import Button from 'react-bootstrap/Button';
import ValidableControl from '../shared/ValidableControl';
import useCurrentUser from '../../hooks/useCurrentUser';
import { put } from '../../utils/network';
import IApiResponse from '../../models/IApiResponse';
import { ApiRoute } from '../../config';

function AccountSettings(): ReactElement {
  const { currentUser, reload: reloadUser } = useCurrentUser();
  const { email: initialEmail, generated_password: noCurrentPasswordRequired } = currentUser;
  const [email, setEmail] = useState(initialEmail);
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [errors, setErrors] = useState({});

  const getErrorMsg = (attr) => (errors[attr] ? errors[attr][0] : '');
  const isInvalid = (attr) => !!errors[attr];

  const submit = async () => {
    const { id: userId } = currentUser;
    const response = await put<IApiResponse>(`${ApiRoute.USERS}/${userId}`, {
      user: {
        email,
        password,
        current_password: currentPassword,
      },
    });

    if (response.status === 422) {
      // @ts-expect-error TODO: fix null safety
      setErrors(response.parsedBody);
    } else {
      setPassword('');
      setPasswordConfirmation('');
      reloadUser();
    }
  };

  useEffect(() => {
    setErrors({});

    if (password && password.length < 8) {
      setErrors({
        password: ['* Password must be at least 8 characters'],
      });
    }

    if (
      password && passwordConfirmation
            && password !== passwordConfirmation
    ) {
      setErrors({
        password: ['* Paswords don’t match'],
        password_confirmation: ['* Paswords don’t match'],
      });
    }
  }, [password, passwordConfirmation]);

  const isSaveEnabled = () => {
    const isCurrentPasswordSatisfied = (currentPassword || noCurrentPasswordRequired);

    const isNewPasswordSatisfied = (password
          && passwordConfirmation
          && password === passwordConfirmation
          && password.length >= 8);

    return isCurrentPasswordSatisfied && isNewPasswordSatisfied && email;
  };

  return (
    <div className="col col-md-9 col-lg-10" style={{ display: 'flex', justifyContent: 'center' }}>
      <div className="c-my-account__settings__container">
        <div className="c-card c-card__content c-my-account__settings__card">
          <div className="c-my-account__settings__header">
            Email & Password
          </div>

          {/* Username Password Content */}
          <div className="c-my-account__settings__content">
            <ValidableControl
              label="Email"
              htmlFor="edit-user-email"
              isInvalid={isInvalid('email')}
              errorMessage={getErrorMsg('email')}
            >
              <input
                id="edit-user-email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </ValidableControl>
            {!noCurrentPasswordRequired && (
            <ValidableControl
              label="Current Password"
              htmlFor="user_current_password"
              isInvalid={isInvalid('current_password')}
              errorMessage={getErrorMsg('current_password')}
            >
              <input
                id="user_current_password"
                type="password"
                value={currentPassword}
                placeholder="Password (minimum 8 characters)"
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </ValidableControl>
            )}
            <ValidableControl
              label="New Password"
              htmlFor="user_password"
              isInvalid={isInvalid('password')}
              errorMessage={getErrorMsg('password')}
            >
              <input
                id="user_password"
                type="password"
                value={password}
                placeholder="Password (minimum 8 characters)"
                onChange={(e) => setPassword(e.target.value)}
              />
            </ValidableControl>
            <ValidableControl
              label="Confirm New Password"
              htmlFor="user_password_confirmation"
              isInvalid={isInvalid('password_confirmation')}
              errorMessage={getErrorMsg('password_confirmation')}
            >
              <input
                id="user_password_confirmation"
                type="password"
                value={passwordConfirmation}
                placeholder="Password (minimum 8 characters)"
                onChange={(e) => setPasswordConfirmation(e.target.value)}
              />
            </ValidableControl>

            <div className="c-my-account__settings__save__container">
              <Button
                className="c-button c-button--primary c-my-account__settings__save__button"
                disabled={!isSaveEnabled() || currentUser.isDeactivated}
                onClick={submit}
              >
                Save Changes
              </Button>
            </div>
          </div>

          <div className="c-my-account__settings__or__dash" />
          <div className="c-my-account__settings__or__text">OR</div>
          <div className="c-my-account__settings__or__dash" />

          {/* Auth buttons */}
          <div className="c-form-group c-my-account__settings__auth__container">
            <div className="c-form-group c-my-account__settings__auth__button">
              <a
                className="c-button c-button--facebook d-block"
                data-cy="connect-with-facebook"
                rel="nofollow"
                href="/users/auth/facebook"
              >
                <FacebookIcon />
                Sign in with Facebook
              </a>
            </div>
            <div className="c-form-group c-my-account__settings__auth__button">
              <a
                className="c-button c-button--google d-block"
                data-cy="connect-with-google"
                href="/users/auth/google_oauth2"
              >
                <GoogleIcon />
                Sign in With Google
              </a>
            </div>
            <div className="c-form-group c-my-account__settings__auth__button">
              <a
                className="c-button c-button--apple d-block c-form-group"
                data-cy="connect-with-apple"
                href="/users/auth/apple"
              >
                <AppleIcon />
                Sign in With Apple
              </a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default AccountSettings;
